import React, { useState, useEffect } from 'react';
import { useUser } from './usersContext';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import './CheckoutPages.css';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import StatusMessage from './components/StatusMessage';
import { useStatusMessage } from './hooks/useStatusMessage';
import ConfirmationModal from './components/ConfirmationModal';
import { useConfirmationModal } from './hooks/useConfirmationModal';
import api from './utils/api';

// Removed debug log to avoid exposing keys in console

// Load Stripe outside of component to avoid recreating it on each render
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// CardForm component to handle Stripe Elements
const CardForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const { currentUser } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cardholderName, setCardholderName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const statusMessage = useStatusMessage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable form submission until Stripe.js has loaded.
      return;
    }
    
    if (!cardholderName.trim()) {
      setError('Please enter the cardholder name');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
        // Create a SetupIntent on the server
      const setupResponse = await api.post('/api/createSetupIntent', {});
      
      if (!setupResponse.ok) {
        throw new Error('Failed to create setup intent');
      }
      
      const { clientSecret } = await setupResponse.json();
      
      // Use the client secret to confirm the SetupIntent
      const { setupIntent, error: setupError } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
          billing_details: {
            name: cardholderName,
            email: currentUser.email
          }
        }
      });
      
      if (setupError) {
        throw new Error(setupError.message);
      }
      
      if (!setupIntent || setupIntent.status !== 'succeeded') {
        throw new Error('Failed to confirm card setup');
      }
        // Complete the payment method setup
      const completeResponse = await api.post('/api/completePaymentMethodSetup', { 
          setupIntentId: setupIntent.id,
          paymentMethodId: setupIntent.payment_method,
          makeDefault
        });
      
      if (!completeResponse.ok) {
        throw new Error('Failed to complete payment method setup');
      }
        const result = await completeResponse.json();
      
      // Show success message if this became the default payment method
      if (result.isDefault && !makeDefault) {
        statusMessage.showSuccess('Payment method added successfully and set as your default payment method');
      } else if (makeDefault) {
        statusMessage.showSuccess('Payment method added successfully and set as your default payment method.');
      } else {
        statusMessage.showSuccess('Payment method added successfully.');
      }
      
      // Call success callback to refresh payment methods and close form
      onSuccess();
    } catch (err) {
      console.error('Error adding payment method:', err);
      setError(err.message || 'Failed to add payment method. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  const cardElementOptions = {
    style: {
      base: {
        fontSize: '16px',
        color: '#424770',
        '::placeholder': {
          color: '#aab7c4',
        },
      },
      invalid: {
        color: '#9e2146',
      },
    },
    hidePostalCode: true,
  };
  
  return (
    <form onSubmit={handleSubmit} style={{ marginTop: '2rem', padding: '2rem', border: '1px solid #ddd', borderRadius: '8px', backgroundColor: '#f8f9fa' }}>
      <h3 style={{ marginBottom: '1.5rem', color: '#333' }}>Add New Payment Method</h3>
      
      <div className="form-group">
        <label htmlFor="cardHolderName">Cardholder Name</label>
        <input
          type="text"
          id="cardHolderName"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name on card"
          required
        />
      </div>
      
      <div className="form-group">
        <label htmlFor="cardElement">Card Details</label>
        <div 
          className="card-element-container" 
          style={{
            padding: '12px',
            border: '1px solid #ddd',
            borderRadius: '6px',
            backgroundColor: 'white'
          }}
        >
          <CardElement id="cardElement" options={cardElementOptions} />
        </div>
      </div>
      
      <div className="form-group checkbox">
        <input
          type="checkbox"
          id="makeDefault"
          checked={makeDefault}
          onChange={(e) => setMakeDefault(e.target.checked)}
        />
        <label htmlFor="makeDefault">Make this my default payment method</label>
      </div>
      
      {error && (
        <div className="payment-error">
          {error}
        </div>
      )}
      
      <div className="form-buttons">
        <button 
          type="button" 
          className="btn-outline"
          onClick={onCancel}
          disabled={loading}
        >
          Cancel
        </button>
        <button 
          type="submit" 
          className="btn-primary"
          disabled={loading || !stripe}
        >
          {loading ? 'Adding...' : 'Add Payment Method'}
        </button>
      </div>
        <div className="secure-payment-notice">
        <p>
          Your payment information is securely processed by Stripe
        </p>
      </div>

      <StatusMessage
        message={statusMessage.message}
        type={statusMessage.type}
        isVisible={statusMessage.isVisible}
        onClose={statusMessage.hideMessage}
        position="top-center"
      />
    </form>
  );
};

const PaymentMethods = ({ initialMessage, onMessageShown }) => {
  const { currentUser, userDoc } = useUser();
  const navigate = useNavigate();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(false); // Start with false to prevent flash
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null); // Track which action is loading
  const [showAddForm, setShowAddForm] = useState(false); // Track whether to show add form
  const [showDisclaimer, setShowDisclaimer] = useState(false); // Added state for disclaimer
  const [hoveredRemoveButton, setHoveredRemoveButton] = useState(null); // Track which remove button is hovered
  const [hasFailedToLoad, setHasFailedToLoad] = useState(false); // Track if initial load failed
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const statusMessage = useStatusMessage();
  const confirmationModal = useConfirmationModal();
  const messageShownRef = React.useRef(false);
  
  // Show initial message if provided (only once)
  useEffect(() => {
    if (initialMessage && initialMessage.text && !messageShownRef.current) {
      messageShownRef.current = true;
      const timer = setTimeout(() => {
        if (initialMessage.type === 'error') {
          statusMessage.showError(initialMessage.text);
        } else if (initialMessage.type === 'warning') {
          statusMessage.showWarning(initialMessage.text);
        } else {
          statusMessage.showInfo(initialMessage.text);
        }
        if (onMessageShown) {
          onMessageShown();
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialMessage]); // Only depend on initialMessage object
  
  // Track window resize for responsive styling
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  useEffect(() => {
    // Redirect tutors to dashboard if they try to access payment methods
    if (userDoc && userDoc.userType === 'tutor') {
      navigate('/dashboard');
      return;
    }
    
    // Only fetch if we have a current user and haven't loaded data yet
    if (currentUser && paymentMethods.length === 0 && !loading && !hasFailedToLoad) {
      fetchPaymentMethods();
    }
  }, [currentUser?.email]); // Only depend on email to avoid unnecessary re-renders
  const fetchPaymentMethods = async (showLoadingState = true, retryCount = 0) => {
    const maxRetries = 3;
    const retryDelay = Math.min(1000 * Math.pow(2, retryCount), 10000); // Exponential backoff, max 10s
    
    try {
      if (showLoadingState) {
        setLoading(true);
      }
      setError(null);      setHasFailedToLoad(false);
      
      // Add timeout to prevent long waits
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await api.post('/api/getPaymentMethods', {}, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error('Failed to fetch payment methods');
      }

      const data = await response.json();
      setPaymentMethods(data.paymentMethods || []);
      
      // Clear any previous error state on success
      setError(null);
      setHasFailedToLoad(false);
    } catch (err) {
      console.error('Error fetching payment methods:', err);
      
      const errorMessage = err.name === 'AbortError' 
        ? 'Request timed out. Please check your connection and try again.'
        : err.message || 'Failed to load payment methods';
      
      // Only show error message after all retries are exhausted
      if (retryCount < maxRetries && err.name !== 'AbortError') {
        setTimeout(() => {
          fetchPaymentMethods(showLoadingState, retryCount + 1);
        }, retryDelay);
        return; // Don't show error yet, we're retrying
      }
      
      // All retries exhausted or timeout - show error via StatusMessage instead of basic error div
      setError(null); // Clear the basic error state
      setHasFailedToLoad(true); // Mark that we failed to load
      statusMessage.showError(errorMessage);
    } finally {
      if (showLoadingState) {
        setLoading(false);
      }
    }
  };

  const handleAddPaymentSuccess = () => {
    setShowAddForm(false);
    // Refresh without showing loading state to prevent flash
    fetchPaymentMethods(false);
  };

  const handleCancelAdd = () => {
    setShowAddForm(false);
  };

  const handleAgreeToDisclaimer = () => {
    setShowDisclaimer(false);
    setShowAddForm(true);
  };

  const handleCancelDisclaimer = () => {
    setShowDisclaimer(false);
  };
  const handleSetDefault = async (paymentMethodId) => {
    try {      setActionLoading(paymentMethodId);
      setError(null);

      const response = await api.post('/api/setDefaultPaymentMethod', { 
          paymentMethodId 
        });

      if (!response.ok) {
        throw new Error('Failed to set default payment method');
      }

      // Refresh payment methods to show updated default (without loading state)
      await fetchPaymentMethods(false);
      statusMessage.showSuccess('Default payment method updated successfully');
    } catch (err) {
      console.error('Error setting default payment method:', err);
      const errorMessage = err.message || 'Failed to set default payment method';
      // Use StatusMessage instead of basic error div
      setError(null);
      statusMessage.showError('Failed to set default payment method. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemove = async (paymentMethodId, isDefault) => {
    // Confirm deletion
    const card = paymentMethods.find(pm => pm.id === paymentMethodId);
    const cardBrand = card?.card?.brand?.toUpperCase() || 'Card';
    const last4 = card?.card?.last4 || '****';
    
    const confirmMessage = isDefault 
      ? `Are you sure you want to remove your default ${cardBrand} ending in ${last4}? ${paymentMethods.length > 1 ? 'Another card will be automatically set as default.' : 'You will need to add a new payment method before reserving sessions.'}`
      : `Are you sure you want to remove this ${cardBrand} ending in ${last4}?`;    // Show confirmation modal instead of window.confirm
    confirmationModal.showConfirmation({
      title: 'Remove Payment Method',
      message: confirmMessage,
      confirmText: 'Remove',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {        try {          setActionLoading(paymentMethodId);
          setError(null);

          const response = await api.post('/api/removePaymentMethod', { 
              paymentMethodId 
            });

          if (!response.ok) {
            throw new Error('Failed to remove payment method');
          }

          // Refresh payment methods (without loading state)
          await fetchPaymentMethods(false);
          statusMessage.showSuccess(`${cardBrand} ending in ${last4} has been removed successfully`);        } catch (err) {
          console.error('Error removing payment method:', err);
          const errorMessage = err.message || 'Failed to remove payment method';
          // Use StatusMessage instead of basic error div
          setError(null);
          statusMessage.showError('Failed to remove payment method. Please try again.');
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const formatCardBrand = (brand) => {
    if (!brand) return 'Card';
    return brand.charAt(0).toUpperCase() + brand.slice(1);
  };

  const formatExpiryDate = (expMonth, expYear) => {
    const month = expMonth.toString().padStart(2, '0');
    const year = expYear.toString().slice(-2);
    return `${month}/${year}`;
  };

  if (!currentUser) {
    return (
      <div className="checkout-content">
        <div>Please log in to manage your payment methods</div>
      </div>
    );
  }

  return (
    <div className="payment-methods-wrapper" style={{ 
      maxWidth: '900px', 
      margin: '0 auto', 
      padding: isMobile ? '1rem 0.5rem' : '2rem' 
    }}>
      <div className="checkout-content" style={{ 
        boxShadow: 'none', 
        background: 'white', 
        padding: isMobile ? '1rem' : '2rem', 
        borderRadius: '10px' 
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '2rem',
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? '1rem' : '0'
        }}>
          <h1 style={{ margin: 0 }}>Payment Methods</h1>
          {!showAddForm && !showDisclaimer && (
            <button
              className="btn-primary"
              onClick={() => setShowDisclaimer(true)}
              style={{ 
                width: isMobile ? '90%' : 'auto', 
                padding: isMobile ? '12px 20px' : '10px 20px'
              }}
            >
              Add Payment Method
            </button>
          )}
        </div>

          {showDisclaimer && (
            <div className="disclaimer-container" style={{ 
              padding: isMobile ? '1rem' : '2rem', 
              border: '1px solid #ddd', 
              borderRadius: '8px', 
              backgroundColor: '#f8f9fa', 
              marginBottom: '2rem' 
            }}>
              <h3 style={{ marginBottom: '1.5rem', color: '#333' }}>Payment Authorization Notice</h3>
              <p>
                By adding a payment method, you authorize Bright Bright Tutors to securely store your payment details and automatically charge your chosen payment method each time you confirm a tutoring session at a rate of $70 per hour. Your payment method will only be charged when a session is explicitly confirmed, and you will receive a receipt via email after each charge.
                <br /><br />
              </p>  
              <p>
                You may review, update, or remove your payment methods at any time through your account settings. Please note that tutoring sessions will only be able to be scheduled with at least one valid payment method attached to your account, and that sessions may only be refunded up to 24 hours before the scheduled start time. 
              <br /><br />
              </p>
              <p>
                If you have any questions or concerns about your billing, charges, or payment methods, please reach out to us at contact@brightbridgetutors.ca
              </p>
              <div className="form-buttons" style={{ marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={handleCancelDisclaimer}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleAgreeToDisclaimer}
                >
                  I Agree
                </button>
              </div>
            </div>
          )}

          {showAddForm && !showDisclaimer && (
            <Elements stripe={stripePromise}>
              <CardForm onSuccess={handleAddPaymentSuccess} onCancel={handleCancelAdd} />
            </Elements>
          )}

          {loading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              Loading payment methods...
            </div>
          ) : paymentMethods.length === 0 && !showAddForm && !showDisclaimer ? ( // Ensure form/disclaimer isn't shown
            <div style={{ textAlign: 'center', padding: isMobile ? '1rem' : '2rem' }}>
              <h3>No Payment Methods Added</h3>
              <p style={{ color: '#6c757d', marginBottom: '2rem' }}>
                You need to add a payment method to reserve tutoring sessions.
              </p>
              <button
                className="btn-primary"
                onClick={() => setShowDisclaimer(true)}
                style={{ 
                  width: isMobile ? '100%' : 'auto', 
                  padding: '12px 24px' 
                }}
              >
                Add Your First Payment Method
              </button>
            </div>
          ) : (
            <div className="payment-methods-list">
              {paymentMethods.map((paymentMethod) => (
                <div 
                  key={paymentMethod.id}
                  style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: isMobile ? '1rem' : '1.5rem',
                    marginBottom: isMobile ? '1.5rem' : '1rem',
                    backgroundColor: paymentMethod.isDefault ? '#f8f9fa' : 'white',
                    borderColor: paymentMethod.isDefault ? '#007bff' : '#ddd',
                  }}
                >
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: isMobile ? 'stretch' : 'flex-start',
                    flexDirection: isMobile ? 'column' : 'row',
                    gap: isMobile ? '1rem' : '0'
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: '10px', 
                        marginBottom: isMobile ? '12px' : '8px',
                        flexWrap: 'wrap'
                      }}>
                        <h3 style={{ 
                          margin: 0, 
                          fontSize: isMobile ? '1rem' : '1.1rem',
                          wordBreak: 'break-word'
                        }}>
                          {formatCardBrand(paymentMethod.card?.brand)} •••• {paymentMethod.card?.last4}
                        </h3>
                        {paymentMethod.isDefault && (
                          <span style={{
                            background: '#007bff',
                            color: 'white',
                            padding: '2px 8px',
                            borderRadius: '12px',
                            fontSize: '0.75rem',
                            fontWeight: '600',
                            whiteSpace: 'nowrap'
                          }}>
                            DEFAULT
                          </span>
                        )}
                      </div>
                      
                      <div style={{ 
                        color: '#6c757d', 
                        fontSize: isMobile ? '0.85rem' : '0.9rem',
                        lineHeight: isMobile ? '1.5' : '1.4'
                      }}>
                        <div>Expires {formatExpiryDate(paymentMethod.card?.exp_month, paymentMethod.card?.exp_year)}</div>
                        {paymentMethod.billing_details?.name && (
                          <div>Cardholder: {paymentMethod.billing_details.name}</div>
                        )}
                      </div>
                    </div>

                    <div style={{ 
                      display: 'flex', 
                      gap: isMobile ? '8px' : '10px', 
                      flexWrap: 'wrap',
                      flexDirection: isMobile ? 'column' : 'row',
                      justifyContent: isMobile ? 'stretch' : 'flex-end',
                      marginTop: isMobile ? '0.75rem' : '0'
                    }}>
                      {!paymentMethod.isDefault && (
                        <button
                          className="btn-outline"
                          onClick={() => handleSetDefault(paymentMethod.id)}
                          disabled={actionLoading === paymentMethod.id}
                          style={{ 
                            width: isMobile ? '100%' : 'auto',
                            flex: isMobile ? '1' : 'none',
                            padding: isMobile ? '12px 16px' : '8px 16px',
                            fontSize: isMobile ? '0.9rem' : '0.9rem',
                            minHeight: isMobile ? '44px' : 'auto',
                            fontWeight: isMobile ? '500' : '400'
                          }}
                        >
                          {actionLoading === paymentMethod.id ? 'Setting...' : 'Set as Default'}
                        </button>
                      )}
                        <button
                        onClick={() => handleRemove(paymentMethod.id, paymentMethod.isDefault)}
                        disabled={actionLoading === paymentMethod.id}
                        style={{
                          background: hoveredRemoveButton === paymentMethod.id ? 'rgba(220, 53, 69, 0.1)' : 'transparent',
                          color: '#dc3545',
                          border: '1px solid #dc3545',
                          borderRadius: '6px',
                          width: isMobile ? '100%' : 'auto',
                          flex: isMobile ? '1' : 'none',
                          padding: isMobile ? '12px 16px' : '8px 16px',
                          fontSize: isMobile ? '0.9rem' : '0.9rem',
                          minHeight: isMobile ? '44px' : 'auto',
                          fontWeight: isMobile ? '500' : '400',
                          cursor: 'pointer',
                          transition: 'all 0.3s',
                        }}
                        onMouseOver={() => setHoveredRemoveButton(paymentMethod.id)}
                        onMouseOut={() => setHoveredRemoveButton(null)}
                      >
                        {actionLoading === paymentMethod.id ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          <div className="secure-payment-notice">
            <p>
              Your payment information is securely stored and processed by Stripe
            </p>
          </div>
          
          <StatusMessage
          message={statusMessage.message}
          type={statusMessage.type}
          isVisible={statusMessage.isVisible}
          onClose={statusMessage.hideMessage}
          position="top-right"
        />

        <ConfirmationModal
          isVisible={confirmationModal.isVisible}
          title={confirmationModal.config.title}
          message={confirmationModal.config.message}
          confirmText={confirmationModal.config.confirmText}
          cancelText={confirmationModal.config.cancelText}
          type={confirmationModal.config.type}
          onConfirm={confirmationModal.handleConfirm}
          onCancel={confirmationModal.handleCancel}
        />
      </div>
    </div>
  );
};

export default PaymentMethods;